<script setup lang="ts">
import type { BrokerProfileCardEmits, BrokerProfileCardProps } from '.';

import { cn } from '~/utils';

import { BrokerAvatar } from '~/components/BrokerAvatar';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';

import IconMaterialSymbolsLightChatBubbleOutlineSharp from '~icons/material-symbols-light/chat-bubble-outline-sharp';
import IconPilarPilarTransparent from '~icons/pilar/pilar-transparent';

import messages from './BrokerProfileCard.locale.json';

const props = withDefaults(defineProps<BrokerProfileCardProps>(), {
	isLoading: false,
	agentName: ''
});

const emit = defineEmits<BrokerProfileCardEmits>();

const strings = messages.pt;

const agentFirstName = computed(() => props.agentName?.split(' ')[0] ?? '');

const quoteText = computed(() =>
	strings.quote.replace('{agentFirstName}', agentFirstName.value)
);

// The hidden handoff copy reserves the same responsive height before and after
// loading, including profiles without an agency header.
const skeletonQuoteText = strings.quote.replace('{agentFirstName}', 'Juliana');

const ctaText = computed(() =>
	strings.cta.replace('{agentFirstName}', agentFirstName.value)
);

const agentCreciText = computed(() =>
	props.agentCreci ? `${strings.creciPrefix} ${props.agentCreci}` : ''
);

const companyCreciText = computed(() =>
	props.companyCreci ? `${strings.creciPrefix} ${props.companyCreci}` : ''
);
</script>

<template>
	<section
		:class="cn('grid w-full', props.class)"
		:aria-busy="isLoading"
		data-testid="broker-profile-card"
	>
		<Skeleton
			is-active
			only-skeleton
			variant="white"
			:class="
				cn(
					'col-start-1 row-start-1 w-full rounded-none motion-reduce:animate-none',
					!isLoading && 'invisible animate-none'
				)
			"
			aria-hidden="true"
		>
			<div class="invisible">
				<div class="h-[85px] md:h-[76px]" />
				<div class="flex flex-col gap-5 px-4 py-5 md:p-8 lg:gap-7">
					<div class="h-14 md:h-20" />
					<p
						class="font-sans text-sm font-normal italic leading-[150%] md:text-base lg:text-lg"
					>
						“{{ skeletonQuoteText }}”
					</p>
					<div class="h-11" />
				</div>
			</div>
		</Skeleton>
		<div v-if="!isLoading" class="col-start-1 row-start-1 flex flex-col">
			<div
				:class="
					cn(
						'flex w-full items-center justify-between gap-3.5 border-b border-secondary-dark-1 bg-secondary-dark-1 px-4 py-5 md:min-h-[76px] md:gap-4 md:border-b-0 md:px-8 md:py-4',
						!companyName && 'max-md:hidden'
					)
				"
				data-testid="broker-profile-card-header"
			>
				<div
					v-if="companyName"
					class="flex min-w-0 items-center gap-3.5 md:gap-3"
				>
					<div
						v-if="companyLogoSrc"
						class="size-9 shrink-0 overflow-hidden rounded-full"
					>
						<NuxtImg
							:src="companyLogoSrc"
							:alt="companyName"
							width="36"
							height="36"
							format="webp"
							loading="lazy"
							decoding="async"
							class="size-full rounded-full object-contain"
						/>
					</div>

					<div class="flex min-w-0 flex-col">
						<p
							class="font-sans text-base font-medium leading-[26px] text-primary"
						>
							{{ strings.companyPrefix }} {{ companyName }}
						</p>
						<p
							v-if="companyCreciText"
							class="font-sans text-xs font-normal leading-[150%] text-primary-light-1"
						>
							{{ companyCreciText }}
						</p>
					</div>
				</div>

				<span
					class="ml-auto hidden h-6 shrink-0 md:flex items-center gap-1 bg-beige px-2 text-center font-sans text-xs font-medium leading-[150%] text-primary"
				>
					<span class="flex size-4 shrink-0 items-center justify-center">
						<IconPilarPilarTransparent
							class="h-[13.33px] w-[14.5px] fill-primary [fill-rule:evenodd]"
						/>
					</span>
					{{ strings.partnerBadge }}
				</span>
			</div>

			<div
				class="flex w-full flex-1 flex-col gap-5 bg-secondary px-4 py-5 md:p-8 lg:gap-7"
			>
				<div class="flex items-center gap-4">
					<BrokerAvatar
						class="size-14 shrink-0 md:size-20"
						avatar-shape="circle"
						loading="lazy"
						:show-company-avatar="false"
						:broker-src="agentPictureSrc || ''"
						:broker-name="agentName"
						:company-name="companyName"
					/>

					<div class="flex min-w-0 flex-col">
						<p
							class="font-matter-sq text-xl font-normal leading-[150%] tracking-[-0.02em] text-primary"
						>
							{{ agentName }}
						</p>
						<p
							v-if="agentCreciText"
							class="font-sans text-sm font-normal leading-[150%] text-primary-light-1"
						>
							{{ agentCreciText }}
						</p>
					</div>
				</div>

				<p
					class="font-sans text-sm font-normal italic md:text-base lg:text-lg leading-[150%] text-primary-light-3"
				>
					“{{ quoteText }}”
				</p>

				<Button
					data-testid="broker-profile-card-button"
					variant="secondary"
					size="sm"
					:is-client-only="false"
					:is-skeleton-loading="false"
					skeleton-classes="w-fit"
					class="w-auto font-sans text-sm font-medium leading-5 text-primary"
					@click="emit('contactClick')"
				>
					<IconMaterialSymbolsLightChatBubbleOutlineSharp class="size-5" />
					{{ ctaText }}
				</Button>
			</div>
		</div>
	</section>
</template>
